import { useNavigate } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { MoreVertical, Eye, Pencil, Trash2, ToggleLeft, QrCode } from 'lucide-react'
import type { Libro } from '../types'

interface LibroCardProps {
  libro: Libro
  onToggleStatus: (id: number) => void
  onDelete: (id: number) => void
}

export function LibroCard({ libro, onToggleStatus, onDelete }: LibroCardProps) {
  const navigate = useNavigate()

  return (
    <Card className="border border-gray-300 dark:border-gray-700 overflow-hidden">
      {/* Portada */}
      <div className="relative h-40 bg-gradient-to-br from-teal-500/20 to-teal-800/20 flex items-center justify-center">
        {libro.imagen_portada_url ? (
          <img
            src={libro.imagen_portada_url}
            alt={libro.nombre}
            className="h-full w-full object-cover"
          />
        ) : (
          <QrCode className="h-16 w-16 text-teal-500/40" />
        )}
        <div className="absolute top-2 right-2 flex gap-1">
          <Badge className={libro.activo ? 'badge-gradient' : 'bg-gray-500 text-white'}>
            {libro.activo ? 'Activo' : 'Inactivo'}
          </Badge>
        </div>
      </div>

      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between">
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-sm truncate">{libro.nombre}</h3>
            {libro.descripcion && (
              <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{libro.descripcion}</p>
            )}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate(`/libro-qr/${libro.id}`)}>
                <Eye className="mr-2 h-4 w-4" /> Ver detalle
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(`/libro-qr/${libro.id}/editar`)}>
                <Pencil className="mr-2 h-4 w-4" /> Editar
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onToggleStatus(libro.id)}>
                <ToggleLeft className="mr-2 h-4 w-4" /> {libro.activo ? 'Desactivar' : 'Activar'}
              </DropdownMenuItem>
              <DropdownMenuItem className="text-red-600" onClick={() => onDelete(libro.id)}>
                <Trash2 className="mr-2 h-4 w-4" /> Eliminar
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Mini stats */}
        <div className="grid grid-cols-3 gap-2 text-center">
          <div>
            <p className="text-lg font-bold text-black dark:text-white">{libro.total_codigos}</p>
            <p className="text-[10px] text-muted-foreground">Códigos</p>
          </div>
          <div>
            <p className="text-lg font-bold text-black dark:text-white">{libro.codigos_canjeados}</p>
            <p className="text-[10px] text-muted-foreground">Canjeados</p>
          </div>
          <div>
            <p className="text-lg font-bold text-black dark:text-white">{(libro.tasa_canje ?? 0).toFixed(1)}%</p>
            <p className="text-[10px] text-muted-foreground">Tasa</p>
          </div>
        </div>

        <Button
          variant="outline"
          size="sm"
          className="w-full"
          onClick={() => navigate(`/libro-qr/${libro.id}`)}
        >
          Ver detalle
        </Button>
      </CardContent>
    </Card>
  )
}
